import { Routes, Route, Navigate } from 'react-router-dom'
import { Box } from '@mui/material'

// Layout components
import Layout from '@components/layout/Layout'
import AuthLayout from '@components/layout/AuthLayout'
import MarketingLayout from '@components/layout/MarketingLayout'
import AdminLayout from '@components/layout/AdminLayout'

// Marketing Pages
import HomePage from '@pages/marketing/HomePage'
import FeaturesPage from '@pages/marketing/FeaturesPage'
import PricingPage from '@pages/marketing/PricingPage'
import AboutPage from '@pages/marketing/AboutPage'
import BlogPage from '@pages/marketing/BlogPage'
import BlogArticlePage from '@pages/marketing/BlogArticlePage'
import ContactPage from '@pages/marketing/ContactPage'
import FAQPage from '@pages/marketing/FAQPage'
import PrivacyPage from '@pages/marketing/PrivacyPage'
import TermsPage from '@pages/marketing/TermsPage'

// Application Pages
import Dashboard from '@pages/Dashboard'
import Collection from '@pages/Collection'
import CigarDetail from '@pages/collection/CigarDetail'
import Humidors from '@pages/Humidors'
import SmokingJournal from '@pages/SmokingJournal'
import SessionDetail from '@pages/SessionDetail'
import Analytics from '@pages/Analytics'
import PreferenceAnalytics from '@pages/PreferenceAnalytics'
import Login from '@pages/auth/Login'
import Register from '@pages/auth/Register'
import OAuthCallback from '@pages/auth/OAuthCallback'
import ForgotPassword from '@pages/auth/ForgotPassword'
import ResetPassword from '@pages/auth/ResetPassword'
import ConfirmEmailChange from '@pages/auth/ConfirmEmailChange'
import Profile from '@pages/Profile'
import Settings from '@pages/Settings'
import NotificationSettings from '@pages/settings/NotificationSettings'
import DatabaseSearch from '@pages/DatabaseSearch'
import DatabaseCigarDetail from '@pages/database/CigarDetail'
import HelpCenter from '@pages/HelpCenter'
import Wishlist from '@pages/Wishlist'
import FeedbackPage from '@pages/Feedback'
import History from '@pages/history/History'
import { SessionManagement } from '@pages/SessionManagement'
import Boxes from '@pages/Boxes'
import ImportData from '@pages/import/ImportData'
import SubscriptionPlans from '@pages/SubscriptionPlans'

// Admin Pages
import AdminDashboard from '@pages/AdminDashboard'
import AdminUserManagement from '@pages/AdminUserManagement'
import AdminSubmissions from '@pages/AdminSubmissions'
import AdminAuditLogs from '@pages/AdminAuditLogs'
import AdminRoleManagement from '@pages/AdminRoleManagement'

// Submission Pages
import SubmitCigar from '@pages/SubmitCigar'
import AdminModeration from '@pages/AdminModeration'
import AdminFeedbackDashboard from '@pages/admin/FeedbackDashboard'
import AdminBrandManagement from '@pages/admin/BrandManagement'
import AdminCigarLineManagement from '@pages/admin/CigarLineManagement'
import AdminCatalogHealth from '@pages/admin/CatalogHealth'
import AdminSubscriptionManagement from '@pages/admin/SubscriptionManagement'
import AdminBackupManagement from '@pages/admin/BackupManagement'
import AdminContentManagement from '@pages/admin/ContentManagement'
import AdminSystemSettings from '@pages/admin/SystemSettings'
import AdminIntegrationsManagement from '@pages/admin/IntegrationsManagement'

// Other Pages
import NotFound from '@pages/NotFound'
import GoogleAnalyticsRouteTracker from '@components/GoogleAnalyticsRouteTracker'

// Protected Route component
import ProtectedRoute from '@components/auth/ProtectedRoute'
import AdminRoute from '@components/auth/AdminRoute'

// Offline connectivity
import { useOnlineStatus } from '@hooks/useOnlineStatus'

function AppContent() {
  useOnlineStatus()
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <GoogleAnalyticsRouteTracker />
      <Routes>
        {/* Marketing routes */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogArticlePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Route>

        {/* Authentication routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="success" element={<OAuthCallback />} />
          <Route index element={<Navigate to="/auth/login" replace />} />
        </Route>

        {/* Application routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="collection" element={<Collection />} />
          <Route path="collection/:id" element={<CigarDetail />} />
          <Route path="humidors" element={<Humidors />} />
          <Route path="journal" element={<SmokingJournal />} />
          <Route path="journal/:id" element={<SessionDetail />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="preferences" element={<PreferenceAnalytics />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/sessions" element={<SessionManagement />} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
          <Route path="settings/subscription" element={<SubscriptionPlans />} />
          <Route path="subscriptions" element={<Navigate to="/app/settings/subscription" replace />} />
          <Route path="boxes" element={<Boxes />} />
          <Route path="import" element={<ImportData />} />
          <Route path="database" element={<DatabaseSearch />} />
          <Route path="database/cigars/:id" element={<DatabaseCigarDetail />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="database/submit" element={<SubmitCigar />} />
          <Route path="help" element={<HelpCenter />} />
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="history" element={<History />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<AdminUserManagement />} />
          <Route path="brands" element={<AdminBrandManagement />} />
          <Route path="cigar-lines" element={<AdminCigarLineManagement />} />
          <Route path="catalog-health" element={<AdminCatalogHealth />} />
          <Route path="subscriptions" element={<AdminSubscriptionManagement />} />
          <Route path="backups" element={<AdminBackupManagement />} />
          <Route path="content" element={<AdminContentManagement />} />
          <Route path="submissions" element={<AdminSubmissions />} />
          <Route path="moderation" element={<AdminModeration />} />
          <Route path="feedback" element={<AdminFeedbackDashboard />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="roles" element={<AdminRoleManagement />} />
          <Route path="settings" element={<AdminSystemSettings />} />
          <Route path="integrations" element={<AdminIntegrationsManagement />} />
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />

        {/* 404 catch all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Box>
  )
}

function App() {
  return <AppContent />
}

export default App
